import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

const GUEST_OWNER = 'guest';

// Cart store. On a connected build, mutations here should also call
// POST/PATCH/DELETE /api/cart so the server stays the source of truth.
//
// Carts are kept per account (`itemsByOwner`, keyed by user id, 'guest'
// for logged-out browsing) so switching accounts on the same browser
// doesn't leak one user's cart into another's, while still letting a
// user's own cart survive logout/login.
export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [], // { book, quantity }
      couponCode: null,
      itemsByOwner: {},
      currentOwner: GUEST_OWNER,

      addItem: (book) => {
        const items = get().items;
        if (items.some((i) => i.book.id === book.id)) return;
        set({ items: [...items, { book, quantity: 1 }] });
        // Fire-and-forget analytics ping feeding the homepage Trending score.
        api.post(`/books/${book.id}/cart-event`).catch(() => {});
      },
      removeItem: (bookId) => set({ items: get().items.filter((i) => i.book.id !== bookId) }),
      clearCart: () => set({ items: [], couponCode: null }),
      applyCoupon: (code) => set({ couponCode: code }),

      // Switches the active cart to the given owner (a user id, or
      // 'guest' after logout), stashing the outgoing owner's cart and
      // restoring the incoming owner's previously saved cart, if any.
      setOwner: (ownerId) => {
        const nextOwner = ownerId || GUEST_OWNER;
        const { items, couponCode, itemsByOwner, currentOwner } = get();
        if (nextOwner === currentOwner) return;

        const saved = itemsByOwner[nextOwner] || { items: [], couponCode: null };
        set({
          itemsByOwner: { ...itemsByOwner, [currentOwner]: { items, couponCode } },
          items: saved.items,
          couponCode: saved.couponCode,
          currentOwner: nextOwner,
        });
      },

      subtotal: () => get().items.reduce((sum, i) => sum + i.book.price * i.quantity, 0),
      count: () => get().items.length,
    }),
    { name: 'geniusmind-cart' }
  )
);
